import { alpha, createTheme } from '@mui/material/styles';

// MIKIWAME Light Theme Colors
const colors = {
  background: {
    default: '#f0f2f5', // Light Grey
    paper: 'rgba(255, 255, 255, 0.8)', // Glassmorphic white
    subtle: 'rgba(240, 242, 245, 0.9)',
  },
  primary: {
    main: '#667eea', // Vibrant Blue/Purple (same as dark)
    light: '#7c90ed',
    dark: '#5a6fd8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#764ba2', // Deep Purple (same as dark)
    light: '#a27dbf',
    dark: '#563582',
    contrastText: '#ffffff',
  },
  text: {
    primary: '#1a1a2e', // Dark Blue/Grey
    secondary: '#5a5a72', // Muted Grey
    disabled: '#9ca3af',
  },
  action: {
    active: '#667eea',
    hover: 'rgba(102, 126, 234, 0.08)',
    selected: 'rgba(102, 126, 234, 0.16)',
  },
  success: {
    main: '#10b981', // Adjusted for light bg
  },
  warning: {
    main: '#f59e0b', // Amber
  },
  error: {
    main: '#ef4444', // Red
  },
  info: {
    main: '#3b82f6', // Blue
  },
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: colors.text,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic UI", "Segoe UI", system-ui, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em', color: colors.text.primary },
    h2: { fontWeight: 800, letterSpacing: '-0.02em', color: colors.text.primary },
    h3: { fontWeight: 700, letterSpacing: '-0.01em', color: colors.text.primary },
    h4: { fontWeight: 700, letterSpacing: '-0.01em', color: colors.text.primary },
    h5: { fontWeight: 600, color: colors.text.primary },
    h6: { fontWeight: 600, color: colors.text.primary },
    body1: { lineHeight: 1.6, color: colors.text.primary },
    body2: { lineHeight: 1.6, color: colors.text.secondary },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f0f2f5',
          backgroundImage: 'none',
          backgroundAttachment: 'fixed',
          scrollbarColor: '#c1c1c1 #f0f2f5',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            width: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#c1c1c1',
            minHeight: 24,
            border: '2px solid #f0f2f5',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: colors.primary.main,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: `0 12px 40px -8px ${alpha(colors.primary.main, 0.2)}`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          background: `linear-gradient(45deg, ${colors.primary.main}, ${colors.secondary.main})`,
          boxShadow: `0 4px 14px 0 ${alpha(colors.primary.main, 0.3)}`,
          color: '#ffffff',
          '&:hover': {
            boxShadow: `0 6px 20px 0 ${alpha(colors.primary.main, 0.4)}`,
            transform: 'scale(1.02)',
          },
        },
        outlined: {
          borderColor: colors.primary.main,
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: alpha(colors.primary.main, 0.08),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          backdropFilter: 'blur(4px)',
        },
        filled: {
          border: '1px solid rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: 'none',
          color: colors.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(16px)',
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(30, 30, 50, 0.9)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
  },
});

export default lightTheme;
